
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  Users, 
  BookOpen, 
  Target,
  Brain,
  AlertCircle,
  CheckCircle,
  Clock,
  Lightbulb
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/auth';

interface InsightSummary {
  id: string;
  category: 'learning' | 'performance' | 'engagement' | 'content';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
  recommendations: string[];
  status: 'new' | 'reviewed' | 'implemented';
  created_at: string;
  strength_areas: string[];
  weakness_areas: string[];
  overall_score: number;
}

const AIInsightsDashboard: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [insights, setInsights] = useState<InsightSummary[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchRealInsights();
    }
  }, [user]);

  const fetchRealInsights = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('insights_summary')
        .select('*')
        .eq('user_id', user.id)
        .order('generated_at', { ascending: false });

      if (error) throw error;

      const transformedInsights: InsightSummary[] = (data || []).map(summary => ({
        id: summary.id,
        category: summary.summary_type as 'learning' | 'performance' | 'engagement' | 'content',
        title: `${summary.summary_type.charAt(0).toUpperCase() + summary.summary_type.slice(1)} Analysis`,
        description: `Comprehensive analysis of your ${summary.summary_type} patterns and progress.`,
        impact: summary.overall_score >= 80 ? 'high' : summary.overall_score >= 60 ? 'medium' : 'low',
        confidence: 85, // Default confidence for real data
        recommendations: summary.recommendations || [],
        status: 'new',
        created_at: summary.generated_at,
        strength_areas: summary.strength_areas || [],
        weakness_areas: summary.weakness_areas || [],
        overall_score: summary.overall_score || 0
      }));

      setInsights(transformedInsights);
    } catch (error) {
      console.error('Error fetching insights:', error);
      setInsights([]);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Insights', icon: Brain },
    { id: 'learning', label: 'Learning', icon: BookOpen },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'engagement', label: 'Engagement', icon: Users },
    { id: 'content', label: 'Content', icon: Target }
  ];

  const filteredInsights = selectedCategory === 'all' 
    ? insights 
    : insights.filter(insight => insight.category === selectedCategory);

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new': return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'reviewed': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'implemented': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Learning Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Please sign in to view your personalized learning insights.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-yellow-500" />
            AI Learning Insights
          </h2>
          <p className="text-muted-foreground">Personalized insights based on your learning data</p>
        </div>
        <Button onClick={fetchRealInsights} disabled={isLoading}>
          Refresh Insights
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              {category.label}
            </Button>
          );
        })}
      </div>

      {/* Insights Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{insights.length}</p>
              <p className="text-sm text-muted-foreground">Total Insights</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {insights.filter(i => i.impact === 'high').length}
              </p>
              <p className="text-sm text-muted-foreground">High Impact</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {insights.reduce((acc, i) => acc + i.recommendations.length, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Recommendations</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {insights.length > 0 ? Math.round(insights.reduce((acc, i) => acc + i.overall_score, 0) / insights.length) : 0}%
              </p>
              <p className="text-sm text-muted-foreground">Avg. Performance</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-3 bg-muted rounded w-full mb-2"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredInsights.length > 0 ? (
          filteredInsights.map((insight) => (
            <Card key={insight.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(insight.status)}
                    <CardTitle className="text-lg">{insight.title}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getImpactColor(insight.impact)}>
                      {insight.impact} impact
                    </Badge>
                    <Badge variant="outline">
                      Score: {insight.overall_score}%
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{insight.description}</p>
                
                <div className="space-y-3">
                  {insight.strength_areas.length > 0 && (
                    <div>
                      <h4 className="font-medium text-green-600 mb-2">Strengths:</h4>
                      <div className="flex flex-wrap gap-1">
                        {insight.strength_areas.map((area, index) => (
                          <Badge key={index} variant="outline" className="text-green-600">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {insight.weakness_areas.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-600 mb-2">Areas for Improvement:</h4>
                      <div className="flex flex-wrap gap-1">
                        {insight.weakness_areas.map((area, index) => (
                          <Badge key={index} variant="outline" className="text-red-600">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {insight.recommendations.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Recommendations:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {insight.recommendations.map((rec, index) => (
                          <li key={index} className="text-muted-foreground">{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Performance:</span>
                      <Progress value={insight.overall_score} className="w-20 h-2" />
                      <span>{insight.overall_score}%</span>
                    </div>
                    <Button size="sm" variant="outline">
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Insights Available</h3>
              <p className="text-muted-foreground mb-4">
                Complete courses and quizzes to generate personalized AI insights about your learning progress.
              </p>
              <Button onClick={fetchRealInsights}>
                Generate Insights
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIInsightsDashboard;
